'use client';

import {
  type PointerEventHandler,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { useMergeRefs } from '../../hooks/use-merge-refs';
import { clamp } from '../../utils/clamp';
import { getDecimalCount } from '../../utils/get-decimal-count';
import { indexOfClosest } from '../../utils/index-of-closest';
import { lerp } from '../../utils/lerp';
import { roundToDecimal } from '../../utils/round-to-decimal';
import { SliderRootContext } from './context/slider-root.context';
import { sliderRootRecipe } from './styles/slider-root.styles';
import type {
  SliderRootContextInterface,
  SliderRootPropsInterface,
} from './types/slider-root.types';

const defaultAriaValuetext: NonNullable<SliderRootPropsInterface['ariaValuetext']> = (state) =>
  `${state.valueNow}`;
const defaultTooltipText: NonNullable<SliderRootPropsInterface['tooltipText']> = (state) =>
  `${state.valueNow}`;
const defaultShiftStep: NonNullable<SliderRootPropsInterface['shiftStep']> = (step) => step * 10;

/**
 * Hosts Slider's internal state and the outer `div`.
 *
 * @since 10.0.0
 */
export const SliderRoot = forwardRef<HTMLDivElement, SliderRootPropsInterface>(
  (props: SliderRootPropsInterface, forwardedRef) => {
    const {
      ariaLabel,
      ariaValuetext = defaultAriaValuetext,
      tooltipText = defaultTooltipText,
      tooltipPlacement = 'top',
      children,
      className,
      css: cssProp,
      defaultValue = 0,
      disabled = false,
      displacementDisabled = false,
      reverse = false,
      min = 0,
      max = 100,
      value,
      snapDragDisabled = false,
      step = 1,
      shiftStep = defaultShiftStep,
      onValueChange,
      onPointerDown,
      onPointerUp,
      onPointerMove,
      minDistance = 0,
      ...rest
    } = props;

    const [forceUpdateDependency, setForceUpdateDependency] = useState<number>(0);

    const sliderRef = useRef<HTMLDivElement>();
    const rectRef = useRef<DOMRect>();
    const handles = useRef<Set<HTMLElement>>(new Set());
    const valueToChangeIndexRef = useRef<number>(0);

    const refs = useMergeRefs([forwardedRef, sliderRef]);

    // Clamps and rounds a number based on min, max, steps, and decimal precision
    const clampAndRound = useCallback(
      (n: number) => {
        const decimals = getDecimalCount(step);
        return clamp(
          [min, max],
          roundToDecimal(Math.round((n - min) / step) * step + min, decimals),
        );
      },
      [max, min, step],
    );

    // Clamps and rounds incoming values to prevent invalid positioning
    const prepareValue = useCallback(
      (rawValue?: number | Array<number>) => {
        if (!rawValue) return;

        if (typeof rawValue === 'number') {
          return clampAndRound(rawValue);
        }

        const arrayValue: Array<number> = [];
        for (const [i, n] of Object.entries(rawValue)) {
          arrayValue[Number(i)] = clampAndRound(n);
        }
        return arrayValue;
      },
      [clampAndRound],
    );

    const [sliderValue, setSliderValue] = useControllableValue<number | Array<number>>({
      controlledValue: prepareValue(value) ?? 0,
      uncontrolledValue: prepareValue(defaultValue) ?? 0,
      isControlled: 'value' in props,
      isUncontrolled: 'defaultValue' in props,
      // This is type casted so the union is optional to consumer's handlers
      onChange: onValueChange as (v: number | Array<number>) => void,
      componentName: 'Slider.Root',
      controlledPropName: 'value',
      uncontrolledPropName: 'defaultValue',
    });

    const focusHandle = useCallback(() => {
      const element = [...handles.current][valueToChangeIndexRef.current];
      if (document?.activeElement !== element) {
        element?.focus?.();
      }
    }, []);

    // Calculates the slider's numeric value from a pointer's coordinates
    const getValueFromPointer = useCallback(
      (pointerPosition: number) => {
        const rect = rectRef.current || sliderRef.current!.getBoundingClientRect();
        const percent = (pointerPosition - rect.left) / rect.width;

        // Caches rect to prevent uneeded measurement during drag
        rectRef.current = rect;
        return clamp([min, max], lerp(reverse ? [max, min] : [min, max], percent));
      },
      [max, min, reverse],
    );

    // Pushes all handles before the current handle
    const pushBefore = useCallback(
      (values: Array<number>, value: number, from: number) => {
        const nextValues = [...values];
        for (let i = from - 1; i >= 0; i--) {
          nextValues[i] = value - minDistance;
        }
        return nextValues;
      },
      [minDistance],
    );

    // Pushes all handles after the current handle
    const pushAfter = useCallback(
      (values: Array<number>, value: number, from: number) => {
        const nextValues = [...values];
        for (let i = from + 1; values[i]; i++) {
          nextValues[i] = value + minDistance;
        }
        return nextValues;
      },
      [minDistance],
    );

    const updateValue: SliderRootContextInterface['setValue'] = useCallback(
      (newValue: number, at = 0, { endKey } = {}) => {
        let prev = 0;
        let nextValue: number | Array<number> = 0;

        const next = clampAndRound(newValue);

        if (Array.isArray(sliderValue)) {
          nextValue = [...sliderValue].sort((a, b) => a - b);
          prev = nextValue[at];
        } else {
          nextValue = next;
          prev = sliderValue!;
        }

        // Prevent updates with no change in value
        if (prev === next) return;

        if (!Array.isArray(nextValue)) {
          setSliderValue(nextValue);
          return;
        }

        nextValue[at] = next;
        const direction = prev > next ? 'down' : 'up';
        const before = nextValue[at - 1] ?? min;
        const after = nextValue[at + 1] ?? max;

        if (direction === 'down' && before + minDistance > next) {
          if (endKey) {
            nextValue[at] = clamp([before, max], nextValue[at]);
          } else {
            if (displacementDisabled) {
              nextValue[at] = before + minDistance;
            } else {
              nextValue = pushBefore(nextValue, next, at);
            }
          }
        }

        if (direction === 'up' && after - minDistance < next) {
          if (endKey) {
            nextValue[at] = clamp([min, after], nextValue[at]);
          } else {
            if (displacementDisabled) {
              nextValue[at] = after - minDistance;
            } else {
              nextValue = pushAfter(nextValue, next, at);
            }
          }
        }

        setSliderValue(nextValue);
      },
      [
        clampAndRound,
        sliderValue,
        min,
        max,
        minDistance,
        setSliderValue,
        displacementDisabled,
        pushBefore,
        pushAfter,
      ],
    );

    const handlePointerDown: PointerEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onPointerDown?.(event);

        // Prevents native focus, we manually focus on the appropriate handle
        event.preventDefault();

        // Saves pointerId to prevent uneeded pointer move and up events
        const target = event.target as HTMLElement;

        if (snapDragDisabled && target === sliderRef.current) return;

        const value = getValueFromPointer(event.clientX);
        target.setPointerCapture(event.pointerId);

        if (Array.isArray(sliderValue)) {
          const closestIndex = indexOfClosest(sliderValue, value);
          valueToChangeIndexRef.current = closestIndex;
          updateValue(value, closestIndex);
        } else {
          updateValue(value);
        }

        focusHandle();
        setForceUpdateDependency(value);
      },
      [focusHandle, getValueFromPointer, onPointerDown, sliderValue, snapDragDisabled, updateValue],
    );

    const handlePointerMove: PointerEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onPointerMove?.(event);

        const target = event.target as HTMLElement;
        if (target.hasPointerCapture(event.pointerId)) {
          const value = getValueFromPointer(event.clientX);
          updateValue(value, valueToChangeIndexRef.current);
          setForceUpdateDependency(value);
        }
      },
      [onPointerMove, getValueFromPointer, updateValue],
    );

    const handlePointerUp: PointerEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onPointerUp?.(event);

        const target = event.target as HTMLElement;
        if (target.hasPointerCapture(event.pointerId)) {
          const value = getValueFromPointer(event.clientX);
          updateValue(value, valueToChangeIndexRef.current);
          target.releasePointerCapture(event.pointerId);
          valueToChangeIndexRef.current = 0;
        }

        // Unsets the cached rect for the next pointer event
        rectRef.current = undefined;
      },
      [getValueFromPointer, onPointerUp, updateValue],
    );

    const styles = css(sliderRootRecipe.raw({ snapDragDisabled, disabled }), cssProp);

    const contextValue = useMemo(
      () => ({
        ariaLabel,
        ariaValuetext,
        value: sliderValue,
        setValue: updateValue,
        handles: handles.current,
        step,
        shiftStep,
        reverse,
        disabled,
        forceUpdateDependency,
        min,
        max,
        tooltipPlacement,
        tooltipText,
      }),
      [
        ariaLabel,
        ariaValuetext,
        sliderValue,
        updateValue,
        step,
        shiftStep,
        reverse,
        disabled,
        forceUpdateDependency,
        min,
        max,
        tooltipPlacement,
        tooltipText,
      ],
    );

    return (
      <SliderRootContext.Provider value={contextValue}>
        <div
          data-c11n-component="Slider.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={refs}
          className={cx('c11n', 'c11n_slider__root', styles, className)}
          onPointerDown={disabled ? undefined : handlePointerDown}
          onPointerMove={disabled ? undefined : handlePointerMove}
          onPointerUp={disabled ? undefined : handlePointerUp}
          {...rest}
        >
          {children}
        </div>
      </SliderRootContext.Provider>
    );
  },
);

SliderRoot.displayName = 'Slider.Root';
